import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Bucket,
  GetSalesInsightsAggregatedRequest,
  TendersGroupedAggregate,
  TendersGroupedAggregateResponse,
  useTendersGroupedAggregate,
} from "../../../../api/indicators";

import { DashboardFiltersContext } from "../filters/DashboardFiltersContext";
import { dateRange, formatDateParam } from "../../../../helpers/dateUtils";

import DoubleBarChart, {
  LineBarChartDataItem,
} from "../charts/DoubleBarChart/DoubleBarChart";

import { DashboardContext } from "../../DashboardContext";
import { SettingsContext } from "../../../../context/SettingsContext";
import { getBucketFromRange } from "../../dashboardHelpers";

const SalesByTender = () => {
  const { t } = useTranslation();
  const { calendarPreferences } = useContext(SettingsContext);
  const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
  const { refreshDate } = useContext(DashboardContext);

  const initialStartDate = useMemo(() => filters.periodFrom
    ? filters.periodFrom
    : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate, [filters.periodFrom]);

  const initialEndDate = useMemo(() => filters.periodTo
    ? filters.periodTo
    : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate, [filters.periodTo]);

  const [params, setParams] =
    useState<GetSalesInsightsAggregatedRequest>({
      fields: "amount,volume",
      startDate: formatDateParam(initialStartDate),
      endDate: formatDateParam(initialEndDate),
      locations: filters?.locations?.join(","),
      locationArea: filters?.locationArea?.id,
      businessStartHour: calendarPreferences.daysStart,
      bucket: getBucketFromRange(initialStartDate, initialEndDate)
    });

  const {
    data: groupedAggregateTendersResponse,
    isLoading,
    isError,
    refetch: fetchSalesInsightsGrouped,
  } = useTendersGroupedAggregate<TendersGroupedAggregateResponse>(
    params,
    "tenders/grouped-aggregate"
  );


  useEffect(() => {

    const start = filters.periodFrom
      ? filters.periodFrom
      : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

    const end = filters.periodTo
      ? filters.periodTo
      : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

    setParams((p) => ({
      ...p,
      startDate: formatDateParam(start),
      endDate: formatDateParam(end),
      locations: filters?.locations?.join(","),
      locationArea: filters?.locationArea?.id,
      bucket: getBucketFromRange(start, end)

    }));
  }, [filters, dateRangeConfig]);

  useEffect(() => {
    fetchSalesInsightsGrouped();
  }, [params, fetchSalesInsightsGrouped]);

  useEffect(() => {
    if (refreshDate != null) {
      fetchSalesInsightsGrouped();
    }
  }, [refreshDate, fetchSalesInsightsGrouped]);


  function transformData(
    dataItem: { dateTime: string } & TendersGroupedAggregate
  ): LineBarChartDataItem {
    const result: LineBarChartDataItem = {
      dateTime: new Date(dataItem.dateTime),
      barData: 0,
    };

    dataItem.tendersGrouped.forEach(({ key, fieldData }) => {
      result[key] = fieldData.amount;
      result.barData += fieldData.volume;
    });

    return result;
  }


  return (
    <DoubleBarChart
      title={t("screens.dashboarding.charts.netSalesByTender")}
      currency={groupedAggregateTendersResponse?.currency}
      description={t(
        "screens.dashboarding.charts.includingTax"
      )}
      data={
        groupedAggregateTendersResponse?.tendersGroupedAggregate.map(
          transformData
        ) ?? []
      }
      isLoading={isLoading}
      isError={isError}
      rightAxisLabel={t(
        "screens.dashboarding.charts.volume"
      )}
      leftAxisLabel={t(
        "screens.dashboarding.charts.amount"
      )}
      bucket={params.bucket as Bucket}
      setBucket={(newBucket: Bucket) =>
        setParams((prev) => ({
          ...prev,
          bucket: newBucket,
        }))
      }
      height={460}
      textLink={t(
        "screens.dashboarding.charts.netSalesByTender"
      )}
      linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SageReport.aspx`}
    />
  );
};

export default SalesByTender;
